<script lang="ts">
  import type { TimelineNote } from '../../lib/api/types';
  import { confirm } from '../../lib/stores/confirmDialog';
  import { integer, t } from '../../lib/i18n/index.ts';
  import { NOTE_MAX } from './format';

  // NoteEditor adds, edits, and deletes the member's note on a day, a stay,
  // or a trip (spec 005 FR-042). showText shows the note itself, for the
  // day's note; a stay's or trip's shows in its row.
  let { note, on, showText = false, onsave, ondelete }: {
    note?: TimelineNote;
    // on is what the note is on.
    on: 'day' | 'stay' | 'trip';
    showText?: boolean;
    onsave: (text: string) => Promise<void>;
    ondelete: (id: string) => Promise<void>;
  } = $props();

  const uid = $props.id();
  let editing = $state(false);
  let text = $state('');
  let busy = $state(false);
  let error = $state('');

  function edit() {
    text = note?.text ?? '';
    error = '';
    editing = true;
  }

  async function run(f: () => Promise<void>) {
    busy = true;
    error = '';
    try {
      await f();
      editing = false;
    } catch (e: any) {
      error = e.message;
    } finally {
      busy = false;
    }
  }

  async function remove() {
    if (!note || !(await confirm(t('timelineNoteDeleteConfirm', { on })))) return;
    const id = note.id;
    run(() => ondelete(id));
  }
</script>

{#if editing}
  <form class="w-full space-y-2" onsubmit={(e) => { e.preventDefault(); if (text.trim()) run(() => onsave(text.trim())); }}>
    <textarea class="input w-full text-sm" rows="3" maxlength={NOTE_MAX} bind:value={text} aria-label={t('timelineNoteOn', { on })}
      aria-describedby="{uid}-count"></textarea>
    <div class="flex items-center gap-2 flex-wrap">
      <button type="submit" class="btn btn-primary btn-sm" disabled={busy || !text.trim()}>{t('timelineSaveNote')}</button>
      <button type="button" class="btn btn-secondary btn-sm" onclick={() => (editing = false)}>{t('appCancel')}</button>
      {#if note}<button type="button" class="btn btn-secondary btn-sm" disabled={busy} onclick={remove}><i aria-hidden="true" class="las la-trash mr-1"></i>{t('timelineDeleteNote')}</button>{/if}
      <span id="{uid}-count" class="ml-auto text-xs text-text-muted tabular-nums">{t('timelineNoteLength', { length: integer(text.length), max: integer(NOTE_MAX) })}</span>
    </div>
    {#if error}<p class="text-sm" style="color: var(--theme-error);">{error}</p>{/if}
  </form>
{:else}
  {#if showText && note}
    <p class="w-full text-sm whitespace-pre-wrap break-words"><i aria-hidden="true" class="las la-sticky-note mr-1 text-text-muted"></i>{note.text}</p>
  {/if}
  <button class="btn btn-secondary btn-sm" onclick={edit}>
    <i aria-hidden="true" class="las la-sticky-note mr-1"></i>{note ? t('timelineEditNote') : t('timelineAddNote')}
  </button>
  {#if error}<p class="w-full text-sm" style="color: var(--theme-error);">{error}</p>{/if}
{/if}
